/** Public site base URL for canonicals and social previews (no trailing slash). */
export function getPublicBaseUrl(): string {
  const fromEnv = process.env.REACT_APP_SITE_URL?.trim().replace(/\/$/, '');
  const pathPrefix = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  if (fromEnv) return `${fromEnv}${pathPrefix}`;
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${pathPrefix}`;
  }
  return pathPrefix || '';
}

export const SITE_NAME = 'BMCS';
export const SITE_FULL_NAME = 'Bothasig Muslim and Cultural Society';

export const DEFAULT_DESCRIPTION =
  'Bothasig Muslim and Cultural Society (BMCS) — serving the Muslim community in Milnerton, Cape Town since 2000.';

export const FACEBOOK_PAGE_URL = 'https://www.facebook.com/BothasigMusallah';

export interface PageSeo {
  title: string;
  description: string;
  noIndex: boolean;
}

const PUBLIC_ROUTES: Record<string, { title: string; description: string }> = {
  '/': {
    title: `${SITE_NAME} - ${SITE_FULL_NAME}`,
    description: DEFAULT_DESCRIPTION,
  },
  '/about': {
    title: `About | ${SITE_NAME}`,
    description:
      'Learn about Bothasig Muslim Cultural Society — mission, constitution, and committee serving Bothasig, Milnerton, and Cape Town since 2000.',
  },
  '/masjid': {
    title: `Our Masjid | ${SITE_NAME}`,
    description:
      'Visit the Bothasig masjid — daily prayers, Jumuah, facilities, and community programs at BMCS in Cape Town.',
  },
  '/classes': {
    title: `Classes & Programs | ${SITE_NAME}`,
    description:
      'Islamic classes at BMCS — youth madrasah, adult learning, Sunday lectures, and Ramadaan programs in Bothasig.',
  },
  '/contact': {
    title: `Contact | ${SITE_NAME}`,
    description:
      'Contact Bothasig Muslim Cultural Society — address, map, and ways to reach our community in Milnerton, Cape Town.',
  },
  '/privacy': {
    title: `Privacy & data | ${SITE_NAME}`,
    description:
      'How BMCS handles personal information: opt-in anonymous page insights, internal accounts, security, and POPIA.',
  },
  '/timeline': {
    title: `History & Timeline | ${SITE_NAME}`,
    description:
      'Explore the history of Bothasig Masjid and BMCS — milestones from our community journey since 2000.',
  },
};

const DASHBOARD_FALLBACK: { title: string; description: string } = {
  title: `Dashboard | ${SITE_NAME}`,
  description: 'BMCS administration — sign in to manage events, notices, and settings.',
};

export function normalizePathname(pathname: string): string {
  const trimmed = pathname.replace(/\/$/, '') || '/';
  return trimmed;
}

const NOT_FOUND_SEO: PageSeo = {
  title: `Page not found | ${SITE_NAME}`,
  description: 'Does not exist.',
  noIndex: true,
};

export function getPageSeo(pathname: string): PageSeo {
  const path = normalizePathname(pathname);
  if (path.startsWith('/dashboard')) {
    return { ...DASHBOARD_FALLBACK, noIndex: true };
  }
  if (path.startsWith('/share/')) {
    return { ...PUBLIC_ROUTES['/'], noIndex: true };
  }
  const entry = PUBLIC_ROUTES[path];
  if (!entry) {
    return NOT_FOUND_SEO;
  }
  return { ...entry, noIndex: false };
}

export function buildShareImageUrl(): string {
  const base = getPublicBaseUrl();
  const path = `${process.env.PUBLIC_URL || ''}/favicon.png`.replace(/\/{2,}/g, '/');
  return `${base}${path}`;
}

export function buildJsonLd(publicBaseUrl: string): object {
  const orgId = `${publicBaseUrl}/#organization`;
  const webId = `${publicBaseUrl}/#website`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': orgId,
        name: SITE_FULL_NAME,
        alternateName: SITE_NAME,
        url: publicBaseUrl,
        logo: `${publicBaseUrl}/favicon.png`,
        description: DEFAULT_DESCRIPTION,
        address: {
          '@type': 'PostalAddress',
          streetAddress: '37 Vryburger Avenue',
          addressLocality: 'Bothasig',
          addressRegion: 'Western Cape',
          addressCountry: 'ZA',
        },
        sameAs: [FACEBOOK_PAGE_URL],
      },
      {
        '@type': 'WebSite',
        '@id': webId,
        url: publicBaseUrl,
        name: SITE_NAME,
        inLanguage: 'en-ZA',
        publisher: { '@id': orgId },
      },
    ],
  };
}
